import * as React from "react";
import { Switch as SwitchPrimitive } from "radix-ui";

import { cn } from "@/v2/lib/utils";

/**
 * The v2 switch.
 *
 * SIZED AND COLOURED INLINE, not with Tailwind utilities, and that is not a
 * style preference. This renders on the Watch page, which is v1 and therefore
 * inside Bootstrap's global cascade: `.v2` scoping does not reach it, and
 * Bootstrap ships unlayered rules that outrank Tailwind's utilities layer (see
 * the notes at the top of v2/styles/v2.css). A width or a padding written as a
 * utility resolves to something else there, silently.
 *
 * The track colour comes from `--v2-primary` with a fallback, so it is the
 * tenant's own blue wherever the token is defined and a sane blue where it is
 * not.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, style, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn("v2-switch", className)}
    style={{
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
      width: 46,
      height: 26,
      padding: 0,
      border: "none",
      borderRadius: 9999,
      cursor: "pointer",
      transition: "background-color 200ms ease",
      background: props.checked
        ? "var(--v2-primary, #0b4ea2)"
        : "#cbd5e1",
      ...style,
    }}
    {...props}
  >
    <SwitchPrimitive.Thumb
      style={{
        display: "block",
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#ffffff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
        // Moved with a transform rather than a margin: a margin on this element
        // is one of the things Bootstrap's reset zeroes out.
        transform: props.checked ? "translateX(23px)" : "translateX(3px)",
        transition: "transform 200ms ease",
        willChange: "transform",
      }}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = "Switch";

export { Switch };
